import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { apiParameters } from 'src/app/configs/api.params';

@Injectable({
  providedIn: 'root',
})
export class ImporterService {
  constructor(private httpClient: HttpClient) {}

  /**
   * Gets all importers
   * @returns All importers
   */
  getAllImporters(): Observable<any> {
    const url = `${apiParameters.urlV2}/importers`;
    return this.httpClient.get(url);
  }

  /**
   * Gets user importers
   * @param userId - user id
   * @param profileId - selected settings profile id
   * @returns User importers
   */
  getUserImporters(userId, profileId?: string): Observable<any> {
    let searchParams = new HttpParams();

    if (profileId && profileId !== '') {
      searchParams = searchParams.append('profileId', profileId);
    }

    const httpOptions = {
      params: searchParams,
    };

    const url = `${apiParameters.urlV2}/users/${userId}/importers`;
    return this.httpClient.get(url, httpOptions);
  }

  /**
   * Adds importer to the user
   * @param userId - user id
   * @param body - is importer data e.g. { "importerId": "ed2addd1-4543-49c5-a075-fdgfdddd", "credentials": { "apiKey": "1AABS@-32332"}}
   * @param profileId - selected settings profile id
   * @returns User importer
   */
  addUserImporter(userId, body, profileId?: string): Observable<any> {
    let searchParams = new HttpParams();

    if (profileId && profileId !== '') {
      searchParams = searchParams.append('profileId', profileId);
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
      params: searchParams,
    };

    const url = `${apiParameters.urlV2}/users/${userId}/importers`;
    return this.httpClient.post(url, body, httpOptions);
  }

  /**
   * Deletes user importer
   * @param userId - user id
   * @param importerId - importer id
   * @param profileId - selected settings profile id
   * @returns User importer
   */
  deleteUserImporter(userId, importerId, profileId?: string): Observable<any> {
    let searchParams = new HttpParams();

    if (profileId && profileId !== '') {
      searchParams = searchParams.append('profileId', profileId);
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
      params: searchParams,
    };

    const url = `${apiParameters.urlV2}/users/${userId}/importers/${importerId}`;
    return this.httpClient.delete(url, httpOptions);
  }

  updateUserImporter(
    userId,
    importerId,
    body,
    profileId?: string
  ): Observable<any> {
    let searchParams = new HttpParams();

    if (profileId && profileId !== '') {
      searchParams = searchParams.append('profileId', profileId);
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
      params: searchParams,
    };

    const url = `${apiParameters.urlV2}/users/${userId}/importers/${importerId}`;
    return this.httpClient.patch(url, body, httpOptions);
  }

  updatePicturePosition(
    presetId: string,
    targetId: string,
    importerId: string,
    body
  ): Observable<any> {
    const url =
      apiParameters.url +
      `/presets/${presetId}/targets/${targetId}/importers/${importerId}`;
    return this.httpClient.patch(url, body);
  }

  deletePicturePosition(
    presetId: string,
    targetId: string,
    importerId: string
  ): Observable<any> {
    const url =
      apiParameters.url +
      `/presets/${presetId}/targets/${targetId}/importers/${importerId}`;
    return this.httpClient.delete(url);
  }

  importVehicles(
    userId: string,
    importerId: string,
    page: number,
    limit: number,
    params?: Record<string, any>
  ): Observable<any> {
    let searchParams = new HttpParams();

    for (const key in params) {
      if (
        params.hasOwnProperty(key) &&
        params[key] !== undefined &&
        params[key] !== ''
      ) {
        searchParams = searchParams.set(key, params[key]);
      }
    }

    const httpOptions = {
      headers: new HttpHeaders({
        page: page?.toString(),
        limit: limit?.toString(),
      }),
      params: searchParams,
    };

    const url = `${apiParameters.urlV2}/users/${userId}/importers/${importerId}/vehicles`;
    return this.httpClient.get(url, httpOptions);
  }

  importMake(userId: string, importerId: string): Observable<any> {
    const url = `${apiParameters.urlV2}/users/${userId}/importers/${importerId}/vehicleMakes`;
    return this.httpClient.get(url);
  }

  importModels(userId: string, importerId: string): Observable<any> {
    const url = `${apiParameters.urlV2}/users/${userId}/importers/${importerId}/vehicleModels`;
    return this.httpClient.get(url);
  }

  importModelsByMake(
    userId: string,
    importerId: string,
    vehicleMakeId: string
  ): Observable<any> {
    const url = `${apiParameters.urlV2}/users/${userId}/importers/${importerId}/vehicleMakes/${vehicleMakeId}/vehicleModels`;
    return this.httpClient.get(url);
  }

  getAllImports(): Observable<any> {
    const url = `${apiParameters.urlV2}/imports`;
    let searchParams = new HttpParams();

    searchParams = searchParams.append('status', 'not_available');

    const httpOptions = {
      params: searchParams,
    };
    return this.httpClient.get(url, httpOptions);
  }

  deleteImports(importId: string): Observable<any> {
    const url = `${apiParameters.urlV2}/imports/${importId}`;

    return this.httpClient.delete(url);
  }

  requestImportImages(
    userId: string,
    importerId: string,
    vehicleId: string,
    body: any,
    profileId?: string
  ): Observable<any> {
    const url = `${apiParameters.urlV2}/users/${userId}/importers/${importerId}/recognition-error/${vehicleId}`;
    let searchParams = new HttpParams();

    if (profileId && profileId !== '') {
      searchParams = searchParams.append('profileId', profileId);
    }

    const httpOptions = {
      params: searchParams,
    };
    return this.httpClient.patch(url, body, httpOptions);
  }

  updateImporter(importerId, body):Observable<any> {
    const url = apiParameters.urlV2 + '/importers/' + importerId;
    return this.httpClient.patch(url, body)
  }

}
